import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { Store as StoreIcon, Star as StarIcon } from "@mui/icons-material";
import React, { memo } from "react";
import { Shop } from "../../../../types/shop";

interface ShopMarkerProps {
  shop: Shop;
  isActive?: boolean;
  onShopPress?: (shop: Shop) => void;
}

const ShopMarker: React.FC<ShopMarkerProps> = memo(
  ({ shop, isActive = false, onShopPress }) => {
    const isGoogleShop = shop.isGooglePlace;

    // Handle shop press
    const handleShopPress = () => {
      if (onShopPress) {
        onShopPress(shop);
      }
    };

    // Get appropriate icon and color
    const IconComponent = isGoogleShop ? StoreIcon : StoreIcon;
    // For non-Google shops, check preference: 0 = Trusted (yellow), 1 = DNU (red)
    const isDNU = !isGoogleShop && shop.preference === 1;
    const normalColor = isGoogleShop
      ? "#4285F4"
      : isDNU
      ? "#F44336" // Red for DNU
      : "#FFA726"; // Yellow for Trusted
    const activeColor = isGoogleShop
      ? "#4285F4"
      : isDNU
      ? "#D32F2F" // Darker red for active DNU
      : "#FFA726"; // Yellow for Trusted

    const InfoSection: React.FC<{
      label: string;
      value: string | null | undefined;
    }> = ({ label, value }) => {
      if (!value) return null;
      return (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {label}:
          </Typography>
          <Typography variant="caption" display="block">
            {value}
          </Typography>
        </Box>
      );
    };

    const tooltipContent = (
      <Stack spacing={0.5}>
        <Typography variant="caption" fontWeight="bold">
          {shop.name}
        </Typography>

        <InfoSection label="Address" value={shop.address || "N/A"} />

        {isGoogleShop ? (
          // Google shop specific info
          <>
            <InfoSection label="Phone" value={shop.phoneNumber} />
            <InfoSection label="Website" value={shop.website} />
            <InfoSection label="Working Hours" value={shop.workingHours} />
            {shop.rating && (
              <InfoSection
                label="Rating"
                value={`${shop.rating.toFixed(1)} ⭐ (${
                  shop.userRatingsTotal || 0
                } reviews)`}
              />
            )}
          </>
        ) : (
          // Trusted shop specific info
          <>
            <InfoSection label="Phone" value={shop.phoneNumber} />
            <InfoSection label="Email" value={shop.email} />
            <InfoSection label="Working Hours" value={shop.workingHours} />
            {shop.description && (
              <>
                <Box sx={{ height: "1px", bgcolor: "divider", my: 0.5 }} />
                <Typography variant="caption">{shop.description}</Typography>
              </>
            )}
          </>
        )}
      </Stack>
    );

    // Base marker styles
    const baseMarkerStyles = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      cursor: "pointer",
      zIndex: isActive ? 2000 : isGoogleShop ? 1800 : 1900,
    };

    // Regular marker styles (no background when not selected)
    const regularMarkerStyles = {
      ...baseMarkerStyles,
      width: 20,
      height: 20,
      backgroundColor: "white",
      border: `2px solid ${normalColor}`,
      borderRadius: "50%",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    };

    // Active marker styles
    const activeMarkerStyles = {
      ...baseMarkerStyles,
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      borderRadius: "20px",
      padding: "10px",
      border: `3px solid ${activeColor}`,
      boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
    };

    const markerStyles = isActive ? activeMarkerStyles : regularMarkerStyles;
    const iconSize = isActive ? 22 : 16;
    const iconColor = isActive ? activeColor : normalColor;

    return (
      <Tooltip
        title={tooltipContent}
        placement="right"
        PopperProps={{
          sx: {
            zIndex: 10000,
          },
          container: document.fullscreenElement || document.body,
        }}
      >
        <Box sx={markerStyles} onClick={handleShopPress}>
          <IconComponent
            sx={{
              fontSize: iconSize,
              color: iconColor,
            }}
          />
        </Box>
      </Tooltip>
    );
  }
);

ShopMarker.displayName = "ShopMarker";

export default ShopMarker;
